import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, FileDown, Eye, Trash2, FileSignature, ArrowRightCircle, Mail, ClipboardList, ShoppingCart, Activity, Clock, CircleCheck, CircleX, MessageCircle, BookmarkPlus, Pencil, Copy, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, EmptyState } from "@/components/Common";
import { EmailDocumentDialog } from "@/components/EmailDocumentDialog";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { ProjectDetailsCard } from "@/components/ProjectDetailsCard";
import { ActivityLog } from "@/components/ActivityLog";
import { fmtMoney, fmtPrice, fmtDate } from "@/lib/format";
import { nextReminderDate, relativeDayHint } from "@/lib/reminders";
import { openWaForQuotation } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function Pill({ value }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    sent: "bg-amber-50 text-amber-800 ring-amber-200",
    accepted: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    rejected: "bg-red-50 text-red-700 ring-red-200",
    expired: "bg-amber-50 text-amber-700 ring-amber-200",
    converted: "bg-amber-50 text-amber-800 ring-amber-200",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ring-1 ring-inset ${map[value] || map.draft}`}>{value}</span>;
}

const MARKUP_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
const emptyLine = () => ({
  item_id: "", qty: 1, cost_price: 0, markup_pct: 0, unit_price: 0,
  line_discount_pct: 0, line_gross: 0, line_discount: 0, line_total: 0,
  name: "", specification: "", unit: "",
  is_manual: false,
});
const emptyManualLine = () => ({
  ...emptyLine(),
  is_manual: true,
  unit: "unit",
});

export default function Quotations() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["quotations"], queryFn: () => api.listQuotations() });
  const { data: reminderSettings } = useQuery({
    queryKey: ["reminder-settings"],
    queryFn: () => api.getReminderSettings(),
    staleTime: 5 * 60_000,
  });
  const quotationInterval = reminderSettings?.quotation_reminder_days || 15;
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: api.listClients });
  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: api.listItems });

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // if set, dialog is in edit mode
  const [emailTarget, setEmailTarget] = useState(null);
  const [projectTarget, setProjectTarget] = useState(null);
  const [activityTarget, setActivityTarget] = useState(null);
  const [filter, setFilter] = useState("all"); // all | pending | approved | rejected
  const [preview, setPreview] = useState(null); // {id, number} or null
  const [form, setForm] = useState({
    client_id: "", tax_rate: 0, currency: "USD", valid_until: "", terms: "", notes: "",
    items: [emptyLine()],
  });

  const bucketOf = (q) => {
    const s = q.status;
    if (s === "accepted" || s === "converted") return "approved";
    if (s === "rejected" || s === "expired") return "rejected";
    return "pending"; // draft, sent, anything else
  };
  const counts = data.reduce(
    (acc, q) => {
      acc[bucketOf(q)] += 1;
      return acc;
    },
    { pending: 0, approved: 0, rejected: 0 }
  );
  // Money totals per bucket, grouped by currency.
  const moneySummary = data.reduce((acc, q) => {
    const bucket = bucketOf(q);
    const cur = q.currency || "USD";
    acc[bucket][cur] = (acc[bucket][cur] || 0) + Number(q.total || 0);
    return acc;
  }, { pending: {}, approved: {}, rejected: {} });
  const filtered = filter === "all" ? data : data.filter((q) => bucketOf(q) === filter);

  function setLine(idx, patch) {
    setForm((f) => {
      const next = [...f.items];
      const cur = { ...next[idx], ...patch };
      // If cost or markup changed and no explicit unit_price override was
      // provided in this patch, recompute unit_price from cost × (1+markup%).
      const cost = Number(cur.cost_price || 0);
      const markup = Number(cur.markup_pct || 0);
      if (!("unit_price" in patch)) {
        cur.unit_price = +(cost * (1 + markup / 100)).toFixed(2);
      }
      const gross = +(Number(cur.qty || 0) * Number(cur.unit_price || 0)).toFixed(2);
      const discPct = Math.max(0, Math.min(100, Number(cur.line_discount_pct || 0)));
      const disc = +(gross * discPct / 100).toFixed(2);
      cur.line_gross = gross;
      cur.line_discount = disc;
      cur.line_total = +Math.max(0, gross - disc).toFixed(2);
      next[idx] = cur;
      return { ...f, items: next };
    });
  }
  function pickItem(idx, item_id) {
    const it = items.find((x) => x.id === item_id);
    if (!it) return setLine(idx, { item_id });
    // Prime the line with the catalog cost; keep the current markup (0 by default).
    setLine(idx, {
      item_id, name: it.name, specification: it.specification, unit: it.unit,
      cost_price: Number(it.price || 0),
    });
  }

  const createMut = useMutation({
    mutationFn: (body) => api.createQuotation(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotations"] });
      setOpen(false);
      setEditingId(null);
      setForm({ client_id: "", tax_rate: 0, currency: "USD", valid_until: "", terms: "", notes: "", items: [emptyLine()] });
      toast.success("Quotation created");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.updateQuotation(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotations"] });
      setOpen(false);
      setEditingId(null);
      setForm({ client_id: "", tax_rate: 0, currency: "USD", valid_until: "", terms: "", notes: "", items: [emptyLine()] });
      toast.success("Quotation updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  // "Duplicate quotation" — clone all fields of an existing quote into a
  // brand-new QT-YYYYMM-XXXX (backend picks the fresh number). Handy for
  // recurring / monthly maintenance quotes so users don't re-type items.
  const duplicateMut = useMutation({
    mutationFn: (q) => api.createQuotation({
      client_id: q.client_id,
      tax_rate: Number(q.tax_rate || 0),
      currency: q.currency || "USD",
      valid_until: "",  // fresh 30-day default from the server
      terms: q.terms || "",
      notes: q.notes || "",
      project_details: q.project_details || {},
      items: (q.items || []).map((l) => ({
        item_id: l.item_id || "",
        is_manual: !l.item_id,
        qty: Number(l.qty || 0),
        cost_price: Number(l.cost_price || 0),
        markup_pct: Number(l.markup_pct || 0),
        line_discount_pct: Number(l.line_discount_pct || 0),
        ...(!l.item_id ? {
          name: l.name || "",
          specification: l.specification || "",
          unit: l.unit || "unit",
        } : {}),
      })),
    }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["quotations"] });
      toast.success(`Duplicated → ${created.quotation_number}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not duplicate"),
  });

  // Prefill the form with an existing quotation and open the dialog in
  // edit mode.  Restores the internal `is_manual` flag from `item_id`
  // presence so the row renders in the compact manual layout.
  const openEdit = (q) => {
    setEditingId(q.id);
    setForm({
      client_id: q.client_id || "",
      quotation_number: q.quotation_number || "",
      tax_rate: Number(q.tax_rate || 0),
      currency: q.currency || "USD",
      valid_until: q.valid_until || "",
      terms: q.terms || "",
      notes: q.notes || "",
      items: (q.items || []).map((it) => ({
        ...emptyLine(),
        ...it,
        is_manual: !it.item_id,
      })),
    });
    setOpen(true);
  };
  const convertMut = useMutation({
    mutationFn: (id) => api.convertQuotation(id),
    onSuccess: (order) => {
      qc.invalidateQueries({ queryKey: ["quotations"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success(`Order created: ${order.order_number}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteQuotation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotations"] });
      toast.success("Deleted");
    },
  });

  // "Save to catalog" — turn a manual quotation line into a real inventory
  // item, then flip the line to be inventory-linked so the same product can
  // be picked from the dropdown next time.
  const saveManualToCatalogMut = useMutation({
    mutationFn: ({ line }) => api.createItem({
      name: (line.name || "").trim(),
      specification: line.specification || "",
      unit: (line.unit || "").trim() || "unit",
      price: Number(line.cost_price || line.unit_price || 0),
      stock_qty: 0,
    }),
    onSuccess: (item, { idx }) => {
      qc.invalidateQueries({ queryKey: ["items"] });
      setForm((f) => {
        const next = [...f.items];
        const cur = next[idx];
        if (!cur) return f;
        next[idx] = {
          ...cur,
          is_manual: false,
          item_id: item.id,
          name: item.name,
          specification: item.specification || cur.specification,
          unit: item.unit || cur.unit || "unit",
          cost_price: Number(item.price || cur.cost_price || 0),
        };
        return { ...f, items: next };
      });
      toast.success(`Saved to inventory: ${item.name}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save item"),
  });

  // Suppliers + Generate-PO dialog state
  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers-for-po"],
    queryFn: () => api.listSuppliers(),
  });
  const [poQuotation, setPoQuotation] = useState(null);
  const [poSupplierId, setPoSupplierId] = useState("");
  const [rfqQuotation, setRfqQuotation] = useState(null);
  const [rfqSupplierId, setRfqSupplierId] = useState("");
  const [rfqValidUntil, setRfqValidUntil] = useState("");
  const createRfqMut = useMutation({
    mutationFn: (body) =>
      api.createRfqFromQuotation(body.quotation_id, {
        supplier_id: body.supplier_id,
        valid_until: body.valid_until || null,
      }),
    onSuccess: (rfq) => {
      qc.invalidateQueries({ queryKey: ["rfqs"] });
      setRfqQuotation(null);
      setRfqSupplierId("");
      setRfqValidUntil("");
      toast.success(`RFQ ${rfq.rfq_number} created`);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Failed to create RFQ"),
  });
  const generatePoMut = useMutation({
    mutationFn: (body) => api.createPurchaseOrderFromQuotation(body),
    onSuccess: (po) => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      setPoQuotation(null);
      setPoSupplierId("");
      toast.success(`Purchase order ${po.po_number} created`);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Failed to generate PO"),
  });

  const subtotal = form.items.reduce((s, l) => s + Number(l.line_total || 0), 0);
  const tax = +(subtotal * Number(form.tax_rate || 0) / 100).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  return (
    <div data-testid="quotations-page">
      <PageHeader
        overline="Quotations"
        title="Quotations"
        subtitle="Pre-sale quotes — convert to an order once accepted by the client."
        actions={
          <Button data-testid="new-quotation-btn" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary/90">
            <Plus className="size-4 mr-1.5" /> New quotation
          </Button>
        }
      />

      {/* Money summary by bucket */}
      {data.length > 0 && (
        <div
          data-testid="quotations-money-summary"
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4"
        >
          <QuoSummaryCard
            testid="quotations-summary-pending"
            label="Pending"
            icon={Clock}
            tone="blue"
            count={counts.pending}
            money={moneySummary.pending}
          />
          <QuoSummaryCard
            testid="quotations-summary-approved"
            label="Approved"
            icon={CircleCheck}
            tone="emerald"
            count={counts.approved}
            money={moneySummary.approved}
          />
          <QuoSummaryCard
            testid="quotations-summary-rejected"
            label="Rejected"
            icon={CircleX}
            tone="red"
            count={counts.rejected}
            money={moneySummary.rejected}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="quotations-tabs">
        {[
          { id: "all", label: "All", count: data.length, tone: "slate" },
          { id: "pending", label: "Pending", count: counts.pending, tone: "blue" },
          { id: "approved", label: "Approved", count: counts.approved, tone: "emerald" },
          { id: "rejected", label: "Rejected", count: counts.rejected, tone: "red" },
        ].map((tab) => {
          const active = filter === tab.id;
          const toneMap = {
            slate: active ? "bg-slate-800 text-white ring-slate-800" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50",
            blue: active ? "bg-primary text-white ring-primary" : "bg-white text-primary ring-primary/20 hover:bg-primary/5",
            emerald: active ? "bg-emerald-600 text-white ring-emerald-600" : "bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50",
            red: active ? "bg-red-600 text-white ring-red-600" : "bg-white text-red-700 ring-red-200 hover:bg-red-50",
          };
          return (
            <button
              key={tab.id}
              data-testid={`quotations-tab-${tab.id}`}
              onClick={() => setFilter(tab.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition ${toneMap[tab.tone]}`}
            >
              {tab.label}
              <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>
      {isLoading ? <p className="text-muted-foreground">{t("loading")}</p>
        : filtered.length === 0 ? <EmptyState title={t("no_data")} hint="Create your first quotation" />
        : (
          <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="quotations-table-wrap">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border">
                  <TableHead className="overline">Quotation #</TableHead>
                  <TableHead className="overline">Client</TableHead>
                  <TableHead className="overline">Issue</TableHead>
                  <TableHead className="overline">Valid until</TableHead>
                  <TableHead className="overline">Status</TableHead>
                  <TableHead className="overline text-right">Total</TableHead>
                  <TableHead className="overline text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((q) => (
                  <TableRow key={q.id} data-testid={`quotation-row-${q.id}`}>
                    <TableCell className="font-medium mono">
                      <span className="flex items-center gap-1.5 text-primary">
                        <FileSignature className="size-3.5" /> {q.quotation_number}
                      </span>
                    </TableCell>
                    <TableCell>{q.client_snapshot?.name}</TableCell>
                    <TableCell className="text-xs mono text-muted-foreground">{fmtDate(q.issue_date)}</TableCell>
                    <TableCell className="text-xs mono text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <span>{fmtDate(q.valid_until)}</span>
                        {(() => {
                          if (!["draft", "sent"].includes(q.status) || q.order_id) return null;
                          const next = nextReminderDate({
                            anchor: q.issue_date,
                            lastRemindedAt: q.reminder_sent_at,
                            intervalDays: quotationInterval,
                          });
                          if (!next) return null;
                          return (
                            <span
                              data-testid={`quotation-next-reminder-${q.id}`}
                              title={`Auto-reminder ${relativeDayHint(next)} (${next})`}
                              className="inline-block px-1.5 py-0.5 rounded-md text-[10px] font-medium ring-1 ring-inset bg-sky-50 text-sky-700 ring-sky-200"
                            >
                              nudge {relativeDayHint(next)}
                            </span>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell><Pill value={q.status} /></TableCell>
                    <TableCell className="text-right mono font-medium">{fmtPrice(q.total, q.currency)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button data-testid={`pdf-quotation-${q.id}`} variant="ghost" size="sm" className="text-primary" onClick={() => setPreview({ id: q.id, number: q.quotation_number })}>
                        <FileDown className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`edit-quotation-${q.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-primary"
                        title="Edit quotation"
                        disabled={!!q.order_id}
                        onClick={() => openEdit(q)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`duplicate-quotation-${q.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-primary"
                        title="Duplicate this quotation"
                        disabled={duplicateMut.isPending}
                        onClick={() => duplicateMut.mutate(q)}
                      >
                        <Copy className="size-3.5" />
                      </Button>
                      <Button data-testid={`project-quotation-${q.id}`} variant="ghost" size="sm" className="text-primary" onClick={() => setProjectTarget(q)} title="Project details">
                        <ClipboardList className="size-3.5" />
                      </Button>
                      <Button data-testid={`email-quotation-${q.id}`} variant="ghost" size="sm" className="text-primary" onClick={() => setEmailTarget(q)} title="Email to client">
                        <Mail className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`wa-quotation-${q.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-emerald-600 hover:text-emerald-700"
                        onClick={async () => {
                          let url = "";
                          try {
                            const r = await api.ensureQuotationPublicLink(q.id);
                            url = r?.approve_url || "";
                          } catch { /* fall back to no-link WA text */ }
                          openWaForQuotation(q, lang, "KINOS", url);
                        }}
                        title="WhatsApp follow-up"
                      >
                        <MessageCircle className="size-3.5" />
                      </Button>
                      <Button data-testid={`po-from-quotation-${q.id}`} variant="ghost" size="sm" className="text-primary" onClick={() => setPoQuotation(q)} title="Generate Purchase Order">
                        <ShoppingCart className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`rfq-from-quotation-${q.id}`}
                        variant="ghost" size="sm"
                        className="text-primary"
                        onClick={() => setRfqQuotation(q)}
                        title="Create Request for Quotation"
                      >
                        <MessageSquareQuote className="size-3.5" />
                      </Button>
                      <Button data-testid={`activity-quotation-${q.id}`} variant="ghost" size="sm" className="text-slate-600" onClick={() => setActivityTarget(q)} title="Activity log">
                        <Activity className="size-3.5" />
                      </Button>
                      {q.order_id ? (
                        <span className="text-[11px] text-amber-800 mono ml-1">→ order</span>
                      ) : (
                        <Button data-testid={`convert-quotation-${q.id}`} variant="ghost" size="sm"
                          className="text-primary" title="Convert to order"
                          onClick={() => convertMut.mutate(q.id)} disabled={convertMut.isPending}>
                          <ArrowRightCircle className="size-3.5" />
                        </Button>
                      )}
                      <Button data-testid={`delete-quotation-${q.id}`} variant="ghost" size="sm"
                        className="text-red-600" onClick={() => deleteMut.mutate(q.id)}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      }

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="quotation-form-dialog" className="sm:max-w-none max-w-none w-[min(96vw,1400px)] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editingId ? "Edit quotation" : "New quotation"}</DialogTitle>
            <DialogDescription className="text-xs">
              {editingId ? "Changes will overwrite the current lines. Line items are re-costed on save." : "Auto-numbered QT-YYYYMM-XXXX. Valid 30 days by default."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Client *</Label>
              <Select value={form.client_id || undefined} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="quo-client-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editingId && (
              <div><Label className="overline">Quotation #</Label>
                <Input data-testid="quo-number" className="mono" value={form.quotation_number || ""} onChange={(e) => setForm({ ...form, quotation_number: e.target.value })} /></div>
            )}
            <div><Label className="overline">Valid until</Label>
              <Input data-testid="quo-valid-until" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
            <div><Label className="overline">Currency</Label>
              <Input data-testid="quo-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
            <div><Label className="overline">Tax rate (%)</Label>
              <Input data-testid="quo-tax-rate" type="number" min="0" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
          </div>

          <div className="mt-3 border border-border rounded-md">
            <div className="px-3 py-2 border-b border-border flex justify-between items-center gap-2">
              <p className="overline">Line items</p>
              <div className="flex items-center gap-2">
                <Button type="button" data-testid="quo-add-line" size="sm" variant="outline"
                  onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyLine()] }))}>
                  <Plus className="size-3.5 mr-1" /> From inventory
                </Button>
                <Button type="button" data-testid="quo-add-manual-line" size="sm" variant="outline"
                  className="border-primary/50 text-primary hover:bg-primary/5"
                  onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyManualLine()] }))}>
                  <Plus className="size-3.5 mr-1" /> Manual entry
                </Button>
              </div>
            </div>
            <div className="p-2 space-y-2">
              <div className="grid gap-2 text-[10px] uppercase tracking-wider text-muted-foreground px-1"
                   style={{ gridTemplateColumns: "minmax(180px,3fr) 70px 110px 90px 80px 110px 120px 70px" }}>
                <div>Item</div>
                <div className="text-right">Qty</div>
                <div className="text-right">Cost</div>
                <div className="text-right">Markup</div>
                <div className="text-right">Disc %</div>
                <div className="text-right">Sell</div>
                <div className="text-right">Total</div>
                <div></div>
              </div>
              {form.items.map((l, idx) => l.is_manual ? (
                // ── Manual line: Item · Description · Qty · Cost · Markup ·
                // Sell · Total · Actions. Sell is auto-computed from
                // cost × (1 + markup%). Discount is intentionally omitted for
                // manual rows to keep the form uncluttered.
                // Custom grid template (not the 12-col shorthand) so numeric
                // cells have enough min-width to fully render 11+ digit
                // amounts without clipping.
                <div key={idx} data-testid={`quo-line-${idx}`}
                     className="grid gap-2 items-start bg-amber-50/40 -mx-2 px-2 py-2 rounded"
                     style={{ gridTemplateColumns: "minmax(140px,2.5fr) minmax(140px,2.5fr) 70px 110px 90px 110px 120px 70px" }}>
                  <div className="min-w-0">
                    <Input
                      data-testid={`quo-line-name-${idx}`}
                      placeholder="Item name (paste or type)"
                      value={l.name}
                      onChange={(e) => setLine(idx, { name: e.target.value })}
                    />
                  </div>
                  <div className="min-w-0">
                    <Textarea
                      data-testid={`quo-line-spec-${idx}`}
                      placeholder="Description (optional — wraps)"
                      value={l.specification}
                      onChange={(e) => setLine(idx, { specification: e.target.value })}
                      rows={1}
                      className="text-xs whitespace-pre-wrap break-words text-justify min-h-10 py-1.5"
                    />
                  </div>
                  <div className="min-w-0">
                    <Input data-testid={`quo-line-qty-${idx}`} type="number" min="0" step="1"
                      value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })}
                      className="mono text-right"
                      title={String(l.qty)} />
                  </div>
                  <div className="min-w-0">
                    <Input data-testid={`quo-line-cost-${idx}`} type="number" min="0" step="0.01"
                      value={l.cost_price}
                      onChange={(e) => setLine(idx, { cost_price: e.target.value })}
                      className="mono text-right"
                      placeholder="Cost"
                      title={String(l.cost_price)} />
                  </div>
                  <div className="min-w-0">
                    <Select
                      value={String(Number(l.markup_pct || 0))}
                      onValueChange={(v) => setLine(idx, { markup_pct: Number(v) })}>
                      <SelectTrigger data-testid={`quo-line-markup-${idx}`} className="mono px-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MARKUP_OPTIONS.map((m) => (
                          <SelectItem key={m} value={String(m)}>+{m}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-right mono text-xs text-muted-foreground pt-2 min-w-0 truncate"
                       data-testid={`quo-line-sell-${idx}`}
                       title={fmtPrice(l.unit_price, form.currency)}>
                    {fmtPrice(l.unit_price, form.currency)}
                  </div>
                  <div className="text-right mono text-sm pt-2 font-medium min-w-0 truncate"
                       data-testid={`quo-line-total-${idx}`}
                       title={fmtPrice(l.line_total, form.currency)}>
                    {fmtPrice(l.line_total, form.currency)}
                  </div>
                  <div className="flex items-center justify-end gap-1 pt-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-emerald-700 hover:bg-emerald-50 h-8 w-8 p-0"
                      title="Save to inventory catalog"
                      data-testid={`quo-line-save-catalog-${idx}`}
                      disabled={
                        !((l.name || "").trim()) ||
                        saveManualToCatalogMut.isPending
                      }
                      onClick={() => saveManualToCatalogMut.mutate({ line: l, idx })}
                    >
                      <BookmarkPlus className="size-3.5" />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="text-red-600 h-8 w-8 p-0"
                      onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}>
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                // ── Inventory line: full cost/markup/discount workflow.
                // Custom px-based grid so numeric fields keep enough room
                // for 11+ digit values without visually clipping.
                <div key={idx} data-testid={`quo-line-${idx}`}
                     className="grid gap-2 items-center"
                     style={{ gridTemplateColumns: "minmax(180px,3fr) 70px 110px 90px 80px 110px 120px 70px" }}>
                  <div className="min-w-0">
                    <Select value={l.item_id || undefined} onValueChange={(v) => pickItem(idx, v)}>
                      <SelectTrigger data-testid={`quo-line-item-${idx}`}><SelectValue placeholder="Select item…" /></SelectTrigger>
                      <SelectContent>
                        {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-0">
                    <Input data-testid={`quo-line-qty-${idx}`} type="number" min="0" step="1"
                      value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })}
                      className="mono text-right" title={String(l.qty)} />
                  </div>
                  <div className="min-w-0">
                    <Input data-testid={`quo-line-cost-${idx}`} type="number" min="0" step="0.01"
                      value={l.cost_price} onChange={(e) => setLine(idx, { cost_price: e.target.value })}
                      className="mono text-right" title={String(l.cost_price)} />
                  </div>
                  <div className="min-w-0">
                    <Select
                      value={String(Number(l.markup_pct || 0))}
                      onValueChange={(v) => setLine(idx, { markup_pct: Number(v) })}>
                      <SelectTrigger data-testid={`quo-line-markup-${idx}`} className="mono">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MARKUP_OPTIONS.map((m) => (
                          <SelectItem key={m} value={String(m)}>+{m}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-0">
                    <Input
                      data-testid={`quo-line-disc-${idx}`}
                      type="number" min="0" max="100" step="0.5"
                      value={l.line_discount_pct || 0}
                      onChange={(e) => setLine(idx, { line_discount_pct: e.target.value })}
                      className="mono text-right"
                      title={`${l.line_discount_pct || 0}%`}
                    />
                  </div>
                  <div className="text-right mono text-xs text-muted-foreground min-w-0 truncate"
                       data-testid={`quo-line-sell-${idx}`}
                       title={fmtPrice(l.unit_price, form.currency)}>
                    {fmtPrice(l.unit_price, form.currency)}
                  </div>
                  <div className="text-right mono text-sm min-w-0 truncate"
                       data-testid={`quo-line-total-${idx}`}
                       title={fmtPrice(l.line_total, form.currency)}>
                    {fmtPrice(l.line_total, form.currency)}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <Button type="button" variant="ghost" size="sm" className="text-red-600 h-8 w-8 p-0"
                      onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}>
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-muted-foreground pt-1 border-t border-border mt-1">
                <span className="font-semibold">Note:</span> markup % is internal — the printed quote only shows the sell price and totals.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-3">
            <div><Label className="overline">Additional terms</Label>
              <Textarea data-testid="quo-terms" rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></div>
            <div><Label className="overline">Notes</Label>
              <Textarea data-testid="quo-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>

          <div className="mt-2 text-right text-sm mono">
            <p>Subtotal: <span data-testid="quo-subtotal">{fmtPrice(subtotal, form.currency)}</span></p>
            <p>Tax: <span data-testid="quo-tax">{fmtPrice(tax, form.currency)}</span></p>
            <p className="font-bold text-primary text-base">Total: <span data-testid="quo-total">{fmtPrice(total, form.currency)}</span></p>
          </div>

          <DialogFooter>
            <Button variant="outline"
              onClick={() => { setOpen(false); setEditingId(null); }}
              data-testid="quo-cancel">Cancel</Button>
            <Button data-testid="quo-save" className="bg-primary hover:bg-primary/90"
              disabled={createMut.isPending || updateMut.isPending}
              onClick={() => {
                const body = {
                  ...form,
                  tax_rate: Number(form.tax_rate || 0),
                  items: form.items
                    .filter((l) => Number(l.qty) > 0 && (l.item_id || (l.is_manual && (l.name || "").trim())))
                    .map((l) => ({
                      item_id: l.item_id || "",
                      is_manual: !!l.is_manual,
                      qty: Number(l.qty),
                      ...(l.is_manual ? {
                        name: (l.name || "").trim(),
                        specification: l.specification || "",
                        unit: (l.unit || "").trim() || "unit",
                      } : {}),
                      cost_price: Number(l.cost_price || 0),
                      markup_pct: Number(l.markup_pct || 0),
                      line_discount_pct: Number(l.line_discount_pct || 0),
                      ...(Number(l.markup_pct) === 0 && Number(l.cost_price) !== Number(l.unit_price)
                        ? { unit_price: Number(l.unit_price) }
                        : {}),
                    })),
                };
                if (editingId) {
                  updateMut.mutate({ id: editingId, body });
                } else {
                  createMut.mutate(body);
                }
              }}>
              {editingId ? "Save changes" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        open={!!preview}
        onOpenChange={(v) => !v && setPreview(null)}
        title={preview ? `Quotation ${preview.number || ""}` : ""}
        filename={preview ? `quotation-${preview.number || preview.id}.pdf` : "quotation.pdf"}
        fetchBlob={preview ? () => api.fetchQuotationPdfBlob(preview.id) : null}
        fetchPngBlob={preview ? () => api.fetchQuotationPngBlob(preview.id) : null}
        pngFilename={preview ? `quotation-${preview.number || preview.id}.png` : null}
      />


      <EmailDocumentDialog
        open={!!emailTarget}
        onOpenChange={(o) => !o && setEmailTarget(null)}
        kind="quotation"
        defaultTo={emailTarget?.client_snapshot?.email || ""}
        defaultClientName={emailTarget?.client_snapshot?.name || ""}
        sendFn={(payload) => api.emailQuotation(emailTarget.id, payload)}
        testIdPrefix="email-quotation"
      />

      <Dialog open={!!projectTarget} onOpenChange={(o) => !o && setProjectTarget(null)}>
        <DialogContent data-testid="quotation-project-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Project details — {projectTarget?.quotation_number}</DialogTitle>
          </DialogHeader>
          {projectTarget && (
            <ProjectDetailsCard
              docId={projectTarget.id}
              initial={projectTarget.project_details}
              patchFn={api.updateQuotation}
              invalidateKeys={[["quotations"]]}
              testIdPrefix="quotation-project"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Activity log for the selected quotation */}
      <Dialog open={!!activityTarget} onOpenChange={(o) => !o && setActivityTarget(null)}>
        <DialogContent data-testid="quotation-activity-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Activity — {activityTarget?.quotation_number}</DialogTitle>
            <DialogDescription className="text-xs">
              Who did what on this quotation.
            </DialogDescription>
          </DialogHeader>
          {activityTarget && (
            <ActivityLog resourceType="quotation" resourceId={activityTarget.id} compact />
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Purchase Order from this quotation */}
      <Dialog open={!!poQuotation} onOpenChange={(v) => { if (!v) { setPoQuotation(null); setPoSupplierId(""); } }}>
        <DialogContent data-testid="quotation-po-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Purchase Order</DialogTitle>
            <DialogDescription className="text-xs">
              From quotation <b className="mono">{poQuotation?.quotation_number}</b> ·{" "}
              {poQuotation?.client_snapshot?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Supplier *</Label>
              <Select
                value={poSupplierId || undefined}
                onValueChange={setPoSupplierId}
              >
                <SelectTrigger data-testid="quotation-po-supplier">
                  <SelectValue placeholder="Pick a supplier…" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {suppliers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No suppliers — add one in Suppliers page first
                    </div>
                  )}
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                Line items and quantities will be copied. Unit costs are pre-filled
                from the catalog — you can adjust them on the PO afterwards.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoQuotation(null)} data-testid="quotation-po-cancel">
              Cancel
            </Button>
            <Button
              data-testid="quotation-po-generate"
              disabled={generatePoMut.isPending || !poSupplierId}
              className="bg-primary hover:bg-primary/90"
              onClick={() =>
                generatePoMut.mutate({
                  quotation_id: poQuotation.id,
                  supplier_id: poSupplierId,
                })
              }
            >
              {generatePoMut.isPending ? "Generating…" : "Generate PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create RFQ from this quotation */}
      <Dialog
        open={!!rfqQuotation}
        onOpenChange={(v) => {
          if (!v) {
            setRfqQuotation(null);
            setRfqSupplierId("");
            setRfqValidUntil("");
          }
        }}
      >
        <DialogContent data-testid="quotation-rfq-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Request for Quotation</DialogTitle>
            <DialogDescription className="text-xs">
              Source items for quotation <b className="mono">{rfqQuotation?.quotation_number}</b>{" "}
              · {rfqQuotation?.client_snapshot?.name}. All items will be copied to
              a new RFQ so a supplier can bid before you commit to a PO.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Primary supplier *</Label>
              <Select
                value={rfqSupplierId || undefined}
                onValueChange={setRfqSupplierId}
              >
                <SelectTrigger data-testid="quotation-rfq-supplier">
                  <SelectValue placeholder="Pick a supplier…" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {suppliers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No suppliers — add one in Suppliers page first
                    </div>
                  )}
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Ask supplier to reply by</Label>
              <Input
                data-testid="quotation-rfq-valid"
                type="date"
                value={rfqValidUntil}
                onChange={(e) => setRfqValidUntil(e.target.value)}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                You can invite more suppliers from the RFQ detail page after it&apos;s created.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRfqQuotation(null)}
              data-testid="quotation-rfq-cancel"
            >
              Cancel
            </Button>
            <Button
              data-testid="quotation-rfq-generate"
              disabled={createRfqMut.isPending || !rfqSupplierId}
              className="bg-primary hover:bg-primary/90"
              onClick={() =>
                createRfqMut.mutate({
                  quotation_id: rfqQuotation.id,
                  supplier_id: rfqSupplierId,
                  valid_until: rfqValidUntil,
                })
              }
            >
              {createRfqMut.isPending ? "Creating…" : "Create RFQ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuoSummaryCard({ testid, label, icon: Icon, tone, count, money }) {
  const styles = {
    blue:    { card: "border-primary/25 bg-primary/5",     iconWrap: "bg-primary/15 text-primary",     num: "text-primary" },
    emerald: { card: "border-emerald-200 bg-emerald-50/60", iconWrap: "bg-emerald-100 text-emerald-700", num: "text-emerald-800" },
    red:     { card: "border-red-200 bg-red-50/60",         iconWrap: "bg-red-100 text-red-700",         num: "text-red-800" },
  };
  const s = styles[tone] || styles.blue;
  const currencies = Object.keys(money || {}).sort();
  return (
    <div data-testid={testid} className={`rounded-md border p-4 ${s.card}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="overline">{label}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {count} quotation{count === 1 ? "" : "s"}
          </p>
        </div>
        <span className={`size-9 grid place-items-center rounded-full ${s.iconWrap}`}>
          <Icon className="size-4" />
        </span>
      </div>
      {currencies.length === 0 ? (
        <p className={`mono text-2xl font-bold ${s.num}`}>$0</p>
      ) : (
        <div className="space-y-0.5">
          {currencies.map((c) => (
            <div key={c} className="flex items-baseline gap-2">
              <p className={`mono text-2xl font-bold leading-tight ${s.num}`}>
                {fmtMoney(money[c])}
              </p>
              <p className="text-[11px] text-muted-foreground font-medium">{c}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

